// Shared score-surface helpers used by both notation surfaces (the Score
// studio page and the Workbench's Score view). Non-component module so
// the component files stay fast-refresh clean.

import { createElement, useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { Clef as EngraverClef } from '@/music/notation/pitch';
import type { Bar } from '@/music/song';
import type { BandArrangement, OptimizedSeqChord } from '@/audio/useBandArrangement';
import { firstPassVoicingsByChordId, flatMeasuresToSequence, songToChart } from '@/music/chart';
import { analyzeHarmony } from '@/music/analysis';
import { AnalysisLaneUnderlay, type AnalysisLaneModel } from '@/components/AnalysisLane';
import type { ScoreSystemOverlayContext, SystemLaneSpaces } from '@/components/ScoreView';
import type { DrawnMeasure } from '@/music/notation/drawn';
import { scoreToStaffSong } from '@/music/score/toSong';
import { songForLowerStaff, songWithoutLyrics, type Song } from '@/music/song';
import type { Score } from '@/music/score/types';
import { topNoteOf } from '@/music/voicing';
import { getChordPlayer } from '@/audio/player';
import { useInspector } from '@/components/inspectorContext';
import { useStore } from '@/state/store';

/** Model clef → engraver clef. An imported part's plain treble draws the
 *  real G clef; 'treble8vb' is the guitar-written convention the app's own
 *  charts read on. A LEAD part stamped plain 'treble' is a legacy app chart
 *  persisted before the clefs split, so it keeps the guitar clef — as does
 *  everything without a stated clef (a chart-only tune has no parts yet)
 *  and percussion. */
export function engraverClef(
  clef: string | undefined,
  partId?: string,
  instrument?: string,
): EngraverClef {
  if (clef === 'bass' || clef === 'alto' || clef === 'tenor' || clef === 'treble8vb') return clef;
  if (clef === 'treble' && (partId !== 'part:lead' || instrument === 'lead')) return 'treble';
  return 'treble8vb';
}

/**
 * The band's playhead mapped onto the WRITTEN score: the flattened playback
 * bar wraps onto its authored measure (repeated bars share ids), and the
 * within-bar offset carries over (both grids count the meter's denominator
 * unit). Null while the band is idle.
 */
export function useBandPlayheadBeat(band: BandArrangement, bars: Bar[]): number | null {
  const { playingIndex, playingMeasureId, playingBarBeat, chart } = band;
  return useMemo(() => {
    if (playingIndex < 0 || !playingMeasureId) return null;
    const writtenIndex = chart.measures.findIndex((mm) => mm.id === playingMeasureId);
    if (writtenIndex < 0 || !bars[writtenIndex]) return null;
    let start = 0;
    for (let i = 0; i < writtenIndex; i++) start += bars[i]?.beats ?? 0;
    // The hook computes beats-into-the-bar against whichever grid produced
    // playingIndex (written, or a pre-expanded playback form).
    return start + Math.min(playingBarBeat, Math.max(0, bars[writtenIndex].beats - 0.01));
  }, [playingIndex, playingMeasureId, playingBarBeat, chart, bars]);
}

/**
 * Make a surface's engraved chord SYMBOLS playable, chart-card style: press
 * strums the optimiser's voicing at that spot (the one the diagram sheet
 * draws), double-click/tap opens the voicing inspector. One hook so the
 * notation pane and the arrangement pane cannot drift apart.
 *
 * Returns spreadable ScoreView props; `{}` when the band has no voicings
 * (chordless tune), so the symbols stay honestly inert.
 */
export function usePlayableChordSymbols(band: BandArrangement): {
  onSelectChord?: (id: string) => void;
  onChordDoubleClick?: (id: string) => void;
  chordActionVerb?: string;
} {
  const chart = useStore((s) => s.chart);
  const { inspect } = useInspector();
  const voicingByChordId = useMemo(
    () => firstPassVoicingsByChordId(chart, band.optimized),
    [chart, band.optimized],
  );
  // The second press of a double-click must not replay — it becomes the
  // inspector (PlayableDiagram's convention, same 280ms window).
  const lastPressRef = useRef({ id: '', at: 0 });
  return useMemo(() => {
    if (voicingByChordId.size === 0) return {};
    const toInspect = (v: OptimizedSeqChord) => ({
      fingering: v.fingering,
      rootDisplay: v.displayRoot,
      chordType: v.chordType,
      symbol: v.symbol,
      inversion: v.inversion,
      stringSet: v.stringSet,
      leadNote: topNoteOf(v.fingering, v.stringSet),
      targetTopNote: v.targetTopNote,
    });
    return {
      chordActionVerb: 'Play',
      onSelectChord: (id: string) => {
        const voicing = voicingByChordId.get(id);
        if (!voicing) return;
        const now = performance.now();
        const last = lastPressRef.current;
        lastPressRef.current = { id, at: now };
        if (last.id === id && now - last.at < 280) return;
        void getChordPlayer().playFingering(voicing.fingering, voicing.stringSet);
      },
      onChordDoubleClick: (id: string) => {
        const voicing = voicingByChordId.get(id);
        if (voicing) inspect(toInspect(voicing));
      },
    };
  }, [voicingByChordId, inspect]);
}

/** One staff of a projected score view, ready for ScoreView's staff stack. */
export interface ProjectedStaff {
  song: Song;
  clef: EngraverClef;
  meta: { label?: string; group?: string };
}

export interface ProjectedScoreView {
  /** The top staff's song — the only one carrying the harmony lane. */
  song: Song;
  extraStaves: { song: Song; clef: EngraverClef }[];
  staffMeta?: { label?: string; group?: string }[];
  /** The top staff's clef, for the engrave options. */
  clef: EngraverClef;
}

/**
 * Project a Score for READING: `part` selects one part by index, or `-1` for
 * the stacked full score (every part's every staff, jointly engraved, part
 * names in the margin — Score Studio's view, now shared so the workbench's
 * notation pane offers the same choices). The harmony lane rides the top
 * staff only; a multi-staff part keeps its group brace.
 */
export function buildScoreView(
  score: Score,
  part: number,
  showLyrics: boolean,
  /** The clef a score with NO staff of its own draws on — a chord-only chart
   *  has zero parts, so a reading edition (B♭'s plain G clef, C bass's F
   *  clef) has nothing to reclef and must say so here; absent = the guitar
   *  clef the app's charts have always read on. */
  defaultClef?: EngraverClef,
): ProjectedScoreView {
  const strip = (song: Song): Song => (showLyrics ? song : songWithoutLyrics(song));
  const parts = score.parts;
  if (part === -1 && parts.length > 1) {
    const entries: ProjectedStaff[] = [];
    parts.forEach((partDef, pi) => {
      partDef.staves.forEach((staffDef, si) => {
        const projected = strip(scoreToStaffSong(score, si, pi));
        entries.push({
          song: entries.length === 0 ? projected : songForLowerStaff(projected),
          clef: engraverClef(staffDef.clef, partDef.id, partDef.instrument),
          meta: {
            ...(si === 0 ? { label: partDef.name || `Part ${pi + 1}` } : {}),
            group: partDef.id,
          },
        });
      });
    });
    return {
      song: entries[0].song,
      extraStaves: entries.slice(1).map(({ song, clef }) => ({ song, clef })),
      staffMeta: entries.map((entry) => entry.meta),
      clef: entries[0].clef,
    };
  }
  // A stale persisted index (a removed part) reads as the LEAD, never a crash
  // — and never as whichever part happens to be last.
  const pi = part >= 0 && part < parts.length ? part : 0;
  const partDef = parts[pi];
  const song = strip(scoreToStaffSong(score, 0, pi));
  const extraStaves = (partDef?.staves.slice(1) ?? []).map((staffDef, i) => ({
    song: strip(scoreToStaffSong(score, i + 1, pi)),
    clef: engraverClef(staffDef.clef, partDef.id, partDef.instrument),
  }));
  return {
    song,
    extraStaves,
    staffMeta:
      partDef && partDef.staves.length > 1
        ? partDef.staves.map(() => ({ group: partDef.id }))
        : undefined,
    clef:
      !partDef?.staves[0]?.clef && defaultClef
        ? defaultClef
        : engraverClef(partDef?.staves[0]?.clef, partDef?.id, partDef?.instrument),
  };
}

/** Vertical space the analysis lane reserves under every system
 *  (staff-spaces): one roman row and one bracket row beneath it. */
const ANALYSIS_LANE_SPACES = 4.0;

/**
 * Analysis-lane props for a ScoreView, or nothing while the toggle is off.
 * Spread the result: `<ScoreView … {...analysisLane} />`. The heavy lifting
 * is analyzeHarmony (pure, corpus-tested); drawing is AnalysisLaneUnderlay.
 */
export function useAnalysisLane(
  song: Song,
  enabled: boolean,
): {
  systemUnderlayHeight?: SystemLaneSpaces;
  renderSystemUnderlay?: (context: ScoreSystemOverlayContext) => ReactNode;
} {
  const lane = useMemo<AnalysisLaneModel | null>(() => {
    if (!enabled) return null;
    // The WRITTEN changes, not the played form: barIndex here must match the
    // engraver's global bar index, and repeats would break that alignment
    // (which is why this is flatMeasuresToSequence, not chartToSequence).
    const chart = songToChart(song);
    const sequence = flatMeasuresToSequence(chart.measures, 'middle', chart.timeSignature);
    if (sequence.length === 0) return null;
    return { sequence, ...analyzeHarmony(sequence, chart.key ?? song.key) };
  }, [song, enabled]);

  const renderSystemUnderlay = useCallback(
    (context: ScoreSystemOverlayContext) =>
      lane ? createElement(AnalysisLaneUnderlay, { context, lane }) : null,
    [lane],
  );
  // Only systems with analysed chords reserve the lane — a chord-less line
  // (an intro, a melody-only passage) keeps the plain stave gap.
  const underlayHeightFor = useCallback(
    (measures: DrawnMeasure[]) =>
      lane &&
      lane.sequence.some((chord) =>
        measures.some((measure) => measure.barIndex === chord.barIndex),
      )
        ? ANALYSIS_LANE_SPACES
        : 0,
    [lane],
  );

  if (!lane) return {};
  return { systemUnderlayHeight: underlayHeightFor, renderSystemUnderlay };
}
